import { useCallback } from "react";
import { Editor, Element as SlateElement, Node, Transforms, Path } from "slate";
import isHotkey from "is-hotkey";
import { HOTKEYS } from "components/ManualBuilder/types";
import { toggleMark } from "components/ManualBuilder/components/toolbar";

export const useKeyboardHandlers = (editor: Editor) => {
  // Helper function to find the outermost list path
  const findOutermostListPath = (startPath: Path): Path => {
    let currentPath = startPath;
    let parentPath = Path.parent(currentPath);
    let isNested = false;

    // Keep going up until we find a non-list parent
    while (parentPath.length > 0) {
      try {
        const parentNode = Node.get(editor, parentPath);
        const isParentList =
          SlateElement.isElement(parentNode) &&
          (parentNode.type === "ul" || parentNode.type === "ol");

        if (!isParentList) {
          // Found a non-list parent, so current path is the outermost list
          break;
        }

        // Still in a list, keep going up
        currentPath = parentPath;
        parentPath = Path.parent(parentPath);
        isNested = true;
      } catch (error) {
        // If we can't get the parent, stop here
        break;
      }
    }

    return currentPath;
  };

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Handle hotkeys
      for (const hotkey in HOTKEYS) {
        if (isHotkey(hotkey, event)) {
          event.preventDefault();
          const mark = HOTKEYS[hotkey];
          toggleMark(editor, mark);
        }
      }

      // Handle Shift key for list indentation
      if (event.key === "Tab" && event.shiftKey) {
        event.preventDefault();
        const { selection } = editor;

        if (selection) {
          // Check if we're in a list item
          const [listItem] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "li",
          });

          if (listItem) {
            const [, listItemPath] = listItem;

            // Find the parent list (ul or ol)
            const [parentList] = Editor.nodes(editor, {
              at: Path.parent(listItemPath),
              match: (n) =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                (n.type === "ul" || n.type === "ol"),
            });

            if (parentList) {
              const [parentNode, parentPath] = parentList;
              const listType = SlateElement.isElement(parentNode)
                ? parentNode.type
                : "ul";

              // Create a new sublist of the same type
              Transforms.wrapNodes(
                editor,
                { type: listType, children: [] } as
                  | import("components/ManualBuilder/types").UnorderedListElement
                  | import("components/ManualBuilder/types").OrderedListElement,
                { at: listItemPath }
              );
            }
            return;
          }
        }
      }

      // Handle Enter key
      if (event.key === "Enter" && !event.shiftKey) {
        const { selection } = editor;

        if (selection) {
          // Check if we're in a title element - prevent all enter inputs
          const [titleElement] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "title",
          });

          if (titleElement) {
            // Prevent enter in title elements entirely
            event.preventDefault();
            return;
          }

          // Check if we're in a message box content element
          const [contentElement] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "content",
          });

          if (contentElement) {
            // Inside a message box content, insert a soft line break
            event.preventDefault();
            editor.insertSoftBreak();
            return;
          }

          // Check if we're in a list item
          const [listItem] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "li",
          });

          if (listItem) {
            event.preventDefault();
            const [node, path] = listItem;

            // Check if the current list item is empty
            const isEmptyListItem = Node.string(node).length === 0;

            if (isEmptyListItem) {
              // Get the current list path
              const currentListPath = Path.parent(path);

              // Find the outermost list path
              const outermostListPath = findOutermostListPath(currentListPath);

              // Remove the empty list item
              Transforms.removeNodes(editor, { at: path });

              // Create a path for inserting a new paragraph after the outermost list
              const insertPath = Path.next(outermostListPath);

              // Insert a new paragraph as a sibling to the outermost list
              Transforms.insertNodes(
                editor,
                { type: "paragraph", children: [{ text: "" }] },
                { at: insertPath }
              );

              // Set cursor to the beginning of the new paragraph
              Transforms.select(editor, Editor.start(editor, insertPath));
            } else {
              // Insert a new list item
              editor.insertBreak();
            }
            return;
          }

          // Default behavior for non-list blocks: reset to normal paragraph
          event.preventDefault();
          editor.insertBreak();
          Transforms.setNodes(
            editor,
            { type: "paragraph" },
            {
              match: (n) =>
                SlateElement.isElement(n) && Editor.isBlock(editor, n),
            }
          );
        }
      }

      // Handle tab key for list indentation and table navigation
      if (event.key === "Tab" && !event.shiftKey) {
        event.preventDefault();
        const { selection } = editor;

        if (selection) {
          // Check if we're in a list item first
          const [listItem] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "li",
          });

          if (listItem) {
            const [, path] = listItem;

            // Get the list type (ul or ol)
            const [parentList] = Editor.nodes(editor, {
              at: Path.parent(path),
              match: (n) =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                (n.type === "ul" || n.type === "ol"),
            });

            if (parentList) {
              const [parentNode] = parentList;
              const listType = SlateElement.isElement(parentNode)
                ? parentNode.type
                : "ul";

              // Create a new sublist of the same type
              Transforms.wrapNodes(
                editor,
                { type: listType, children: [] } as
                  | import("components/ManualBuilder/types").UnorderedListElement
                  | import("components/ManualBuilder/types").OrderedListElement,
                { at: path }
              );
            }
            return;
          }

          // If not in a list item, handle tables
          const [table] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "table",
          });

          if (table) {
            const [cell] = Editor.nodes(editor, {
              match: (n) =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                n.type === "table-cell",
            });

            if (cell) {
              const [, cellPath] = cell;
              const nextPath = [
                ...cellPath.slice(0, -1),
                cellPath[cellPath.length - 1] + 1,
              ];

              try {
                const nextCell = Editor.node(editor, nextPath);
                if (nextCell) {
                  const point = Editor.start(editor, nextPath);
                  Transforms.select(editor, point);
                }
              } catch (error) {
                // If next cell doesn't exist, try moving to the next row
                try {
                  const nextRowPath = [
                    ...cellPath.slice(0, -2),
                    cellPath[cellPath.length - 2] + 1,
                    0,
                  ];
                  const nextRowCell = Editor.node(editor, nextRowPath);
                  if (nextRowCell) {
                    const point = Editor.start(editor, nextRowPath);
                    Transforms.select(editor, point);
                  }
                } catch (error) {
                  // Do nothing if we're at the last cell
                }
              }
            }
          }
        }
      }

      // Handle backspace/delete on empty list items and title elements
      if (event.key === "Backspace" || event.key === "Delete") {
        const { selection } = editor;

        // Check if we're in a title element - prevent deletion when already empty
        const [titleElement] = Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === "title",
        });

        if (titleElement) {
          const [node] = titleElement;
          const titleText = Node.string(node);

          // Prevent deletion if title is already empty (has 0 characters)
          if (titleText.length === 0) {
            event.preventDefault();
            return;
          }
        }

        // Check if we're in a content element - prevent deletion when already empty
        const [contentElement] = Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            n.type === "content",
        });

        if (contentElement) {
          const [node] = contentElement;
          const contentText = Node.string(node);

          // Prevent deletion if content is already empty (has 0 characters)
          if (contentText.length === 0) {
            event.preventDefault();
            return;
          }
        }

        if (selection && selection.anchor.offset === 0) {
          // Check if we're in a list item
          const [listItem] = Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              SlateElement.isElement(n) &&
              n.type === "li",
          });

          if (listItem) {
            const [node, path] = listItem;

            // Only handle empty list items
            if (Node.string(node).length === 0) {
              event.preventDefault();

              // Get the current list path
              const currentListPath = Path.parent(path);

              // Find the outermost list path
              const outermostListPath = findOutermostListPath(currentListPath);

              // Remove the empty list item
              Transforms.removeNodes(editor, { at: path });

              // Create a path for inserting a new paragraph after the outermost list
              const insertPath = Path.next(outermostListPath);

              // Insert a new paragraph as a sibling to the outermost list
              Transforms.insertNodes(
                editor,
                { type: "paragraph", children: [{ text: "" }] },
                { at: insertPath }
              );

              // Set cursor to the beginning of the new paragraph
              Transforms.select(editor, Editor.start(editor, insertPath));
            }
          }
        }
      }
    },
    [editor]
  );

  return onKeyDown;
};
